/**
 * Landingpage „Fachkraft für Musik und Sprache im Elementarbereich".
 *
 * Aufbau, Reihenfolge und Texte folgen dem Prototyp
 * `17_PROTOTYP_Landingpage_Musik-und-Sprache.html` (Drive, MusiKonzept_Knowledge,
 * Stand 28.09.2026). Die Inhalte stehen in `src/config/musikUndSpracheLandingpage.ts`,
 * hier wird nur gerendert.
 *
 * Zwei Dinge weichen bewusst vom Prototyp ab und sind nicht getippt, sondern
 * eingesetzt:
 *   - das Startdatum kommt aus `ausbildungFreischaltung.ts`, damit es mit der
 *     Freischaltung des Lernbereichs und den Kaufmails übereinstimmt;
 *   - die Preise kommen aus `trainingBundlePricing.ts`, und ob der
 *     Frühbucher-Rabatt gilt, sagt der Server (`useAktionsstatus`).
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { ArrowRight, BadgeEuro, Star } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import { TRAINING_BUNDLE_IDS } from "@/config/trainingBundles";
import {
  AKTION_ENDE_LABEL,
  aktionRabattCents,
  anzeigeRaten,
  anzeigeRechnung,
  formatEuro,
  getBundlePricingBySlug,
  type TarifPrices,
} from "@/config/trainingBundlePricing";
import {
  BAUSTEINE,
  HERO_BILD,
  KURSE,
  LIEDER,
  ZAHLEN,
  bausteinIcon,
  faq,
  vimeoPlayerUrl,
  vimeoPausenNachricht,
  type Lied,
} from "@/config/musikUndSpracheLandingpage";
import { useBundleActive } from "@/hooks/useBundleActive";
import { useAktionsstatus } from "@/hooks/useAktionsstatus";
import { useQuery } from "@tanstack/react-query";
import {
  bewertungenDb,
  durchschnittText,
  zeigeBewertungsAbschnitt,
} from "@/lib/ausbildungBewertungen";

const BUNDLE_SLUG = "fachkraft-musik-und-sprache";
const CHECKOUT_PFAD = `/ausbildung/${BUNDLE_SLUG}/kaufen`;
const PRICING = getBundlePricingBySlug(BUNDLE_SLUG);

/*
 * Farben, für die die App keinen eigenen Token hat. Werte aus dem Prototyp.
 * Das dunkle Orange der Kicker ist nötig, weil `accent` als Schrift auf hellem
 * Grund zu wenig Kontrast hat; die hellen Etiketten tragen dunkle Schrift aus
 * demselben Grund.
 */
const KICKER = "mb-1.5 inline-block font-display text-xs font-bold uppercase tracking-[0.14em] text-[#B5720A]";
const FLAECHE_HELL = "bg-[#F4F1EB]";
const TUERKIS_HAUCH = "bg-[#EAF6F7]";
const ETIKETT_TUERKIS = "bg-[#49BDC2] text-[#12494C]";
const ETIKETT_ORANGE = "bg-[#F5A623] text-[#4A3200]";
const FOKUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

/** „490 €" statt „490,00 €", wie im Prototyp; Centbeträge bleiben stehen. */
function euro(cents: number): string {
  return cents % 100 === 0
    ? new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(cents / 100)
    : formatEuro(cents);
}

export default function FachkraftMusikUndSprache() {
  const { data: istAktiv } = useBundleActive(TRAINING_BUNDLE_IDS.FACHKRAFT_MUSIK_SPRACHE);
  const buchbar = istAktiv === true;
  // Frühbucher-Rabatt: der Server sagt, ob er läuft. Bis die Antwort da ist,
  // stehen die regulären Preise da.
  const aktionAktiv = useAktionsstatus(TRAINING_BUNDLE_IDS.FACHKRAFT_MUSIK_SPRACHE);

  const mitglied = PRICING?.tarife.mitglied;
  const extern = PRICING?.tarife.extern;
  const ratenMitglied = mitglied ? anzeigeRaten(mitglied, aktionAktiv) : null;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SEO
        title="Fachkraft für Musik und Sprache im Elementarbereich | Online-Ausbildung"
        description="Sprache fördern, mitten im Kita-Alltag: Online-Ausbildung für pädagogische Fachkräfte mit acht Bausteinen für Krippe und Kita, Abschlussprüfung und Zertifikat."
        path={`/ausbildung/${BUNDLE_SLUG}`}
        image="https://www.musikonzept.de/images/musik-und-sprache/og.jpg"
      />
      <Header />

      <main className="flex-1 overflow-x-clip">
        {/* 1. Hero — Aufbau und Look wie auf den Geschwister-Landingpages */}
        <section className="relative overflow-hidden pb-16 pt-28 min-[900px]:pb-24 min-[900px]:pt-32">
          {/* Sanfter Farbverlauf + schwebende Farbwolken in den Markenfarben */}
          <div className="absolute inset-0 bg-gradient-to-br from-hero-gradient-start via-background to-hero-gradient-end opacity-60" />
          <div className="animate-blob-drift pointer-events-none absolute -left-24 -top-24 h-[420px] w-[420px] rounded-full bg-primary/15 blur-3xl" />
          <div className="animate-blob-drift-delayed pointer-events-none absolute -right-32 top-1/3 h-[480px] w-[480px] rounded-full bg-accent/15 blur-3xl" />
          <div className="animate-blob-drift pointer-events-none absolute bottom-0 left-1/3 h-[360px] w-[360px] rounded-full bg-yellow-300/10 blur-3xl" />

          <div className="relative z-10 mx-auto max-w-[1120px] px-4 min-[421px]:px-5">
            <div className="grid items-center gap-8 min-[900px]:grid-cols-[1.05fr_0.95fr] min-[900px]:gap-14">
              <div className="animate-fade-in">
                <div className="mb-3.5 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 backdrop-blur-sm">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-accent" aria-hidden="true" />
                  <span className="text-[13px] font-semibold text-primary">
                    {/* Seit dem Start am 15.10.2026 ist jede Buchung sofort frei (Frage 296) */}
                    Einstieg jederzeit · dein Zugang ist sofort frei
                  </span>
                </div>
                <span className={cn(KICKER, "block")}>Fachkraft für Musik und Sprache im Elementarbereich</span>
                <h1 className="mb-4 mt-1 font-display text-[clamp(32px,5.2vw,52px)] font-extrabold leading-[1.1] tracking-[-0.02em] text-foreground [text-wrap:balance]">
                  <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                    Sprache fördern,
                  </span>{" "}
                  mitten im Kita{"‑"}Alltag
                </h1>
                <p className="max-w-[66ch] text-lg text-muted-foreground">
                  Sprache entwickelt sich im Alltag: im Morgenkreis, beim Anziehen, beim Essen und im
                  gemeinsamen Spiel. In dieser Online-Ausbildung lernst du, wie du Musik gezielt
                  einsetzen kannst, um Sprache ganz selbstverständlich in den Kita-Alltag zu
                  integrieren und sprachliche Entwicklung spielerisch zu begleiten.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Button
                    size="lg"
                    className="group rounded-full bg-accent px-8 text-accent-foreground shadow-xl transition-all duration-300 hover:scale-105 hover:bg-accent/90 hover:shadow-2xl"
                    asChild
                  >
                    <a href="#preise">
                      Jetzt anmelden
                      <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </a>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="rounded-full border-2 px-8 transition-all duration-300 hover:scale-105 hover:bg-accent/10"
                    asChild
                  >
                    <a href="#kurse">Inhalte ansehen</a>
                  </Button>
                </div>
              </div>

              {/* Bild mit schwebender Kreis-Plakette */}
              <div className="relative animate-fade-in" style={{ animationDelay: "0.2s" }}>
                <div className="relative overflow-hidden rounded-3xl shadow-2xl">
                  <img
                    src={HERO_BILD.src}
                    alt={HERO_BILD.alt}
                    width={HERO_BILD.breite}
                    height={HERO_BILD.hoehe}
                    className="block h-auto w-full max-w-full"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent" />
                </div>
                <div className="absolute -right-3 -top-3 z-20 animate-fade-in min-[900px]:-right-6 min-[900px]:-top-6" style={{ animationDelay: "0.4s" }}>
                  <div className="relative">
                    <div className="absolute inset-0 scale-110 animate-pulse rounded-full bg-accent/40 blur-2xl" aria-hidden="true" />
                    <div className="relative flex h-24 w-24 -rotate-6 flex-col items-center justify-center rounded-full bg-gradient-to-br from-accent to-yellow-400 p-2 text-center text-accent-foreground shadow-2xl transition-transform duration-500 hover:rotate-0 min-[900px]:h-36 min-[900px]:w-36 min-[900px]:p-4">
                      {aktionAktiv ? (
                        <span className="text-[10px] font-bold uppercase leading-tight tracking-wide min-[900px]:text-sm">
                          Frühbucher-<br />Rabatt<br />bis 15.10.
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold uppercase leading-tight tracking-wide min-[900px]:text-sm">
                          Einstieg<br />jederzeit
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Vertrauensleiste wie auf den Geschwisterseiten */}
        <section className="border-y border-border/40 bg-muted/20 py-6">
          <div className="mx-auto max-w-[1120px] px-4 min-[421px]:px-5">
            <div className="flex flex-col items-center justify-center gap-4 text-center sm:flex-row sm:gap-0">
              {/* Keine feste Bewertung ohne Quelle (Frage 297); echte Stimmen zeigt „Das sagen Absolventinnen“ unten. */}
              <a
                href="/foerdermoeglichkeiten"
                className="group flex items-center gap-2 text-foreground transition-colors hover:text-accent sm:px-6"
              >
                <BadgeEuro className="h-5 w-5 text-primary" />
                <span className="font-medium">Förderfähig – bis zu 500 € Zuschuss in NRW</span>
                <ArrowRight className="h-4 w-4 text-accent transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </section>

        {/* 2. Zahlenleiste */}
        <Abschnitt>
          <span className={KICKER}>Was drinsteckt</span>
          <Ueberschrift>Zwei Altersgruppen, acht Bausteine, ein Lernbereich</Ueberschrift>
          <Lede>
            Kati Breuer begleitet dich durch die Krippe, Nicole Knorr durch die Kita. Beide arbeiten dieselben acht
            Bausteine durch, altersgerecht gefüllt. Dazu erklärt Tabitha Thieme die Grundlagen der Sprachentwicklung.
          </Lede>
          <dl className="mt-7 grid grid-cols-2 gap-3 min-[900px]:grid-cols-5">
            {ZAHLEN.map((z) => (
              <div
                key={z.text}
                className="flex flex-col-reverse rounded-xl border border-primary/15 bg-gradient-to-br from-primary/5 to-accent/5 px-3.5 py-4"
              >
                <dt className="mt-1.5 text-[13px] leading-snug text-muted-foreground">{z.text}</dt>
                <dd className="font-display text-[27px] font-extrabold leading-none text-primary [font-variant-numeric:tabular-nums]">
                  {z.wert}
                </dd>
              </div>
            ))}
          </dl>
        </Abschnitt>

        {/* 3. Die vier Kurse */}
        <Abschnitt id="kurse" hell>
          <span className={KICKER}>Deine Kurse</span>
          <Ueberschrift>Vier Kurse, aufeinander aufgebaut</Ueberschrift>
          <Lede>
            Zuerst verstehst du, wie Sprache bei Kindern wächst. Dann holst du dir die Praxis: ein Kurs für die Krippe,
            einer für die Kita. Und der vierte trägt dich musikalisch durchs ganze Kita-Jahr.
          </Lede>
          <div className="mt-7 grid grid-cols-1 gap-[18px] min-[521px]:grid-cols-2 min-[1001px]:grid-cols-4">
            {KURSE.map((k) => (
              <article
                key={k.slug}
                className="flex flex-col overflow-hidden rounded-[14px] border bg-card shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <img
                  src={k.bild}
                  alt={k.bildAlt}
                  loading="lazy"
                  className="block aspect-[900/502] w-full max-w-full object-cover"
                />
                <div className="flex flex-1 flex-col px-[18px] pb-5 pt-4">
                  <div className="mb-1.5 font-display text-[11px] font-bold uppercase tracking-[0.1em] text-primary">
                    Kurs {k.nummer}
                  </div>
                  <h3 className="mb-2 font-display text-[17px] font-bold leading-snug text-foreground">{k.titel}</h3>
                  <p className="mb-3 flex-1 text-sm text-muted-foreground">{k.text}</p>
                  <div className="border-t pt-2.5 text-[13px] font-semibold text-muted-foreground">{k.dozentin}</div>
                </div>
              </article>
            ))}
          </div>
        </Abschnitt>

        {/* 4. Die acht Bausteine */}
        <Abschnitt id="bausteine">
          <span className={KICKER}>Klick dich durch</span>
          <Ueberschrift>Die acht Bausteine</Ueberschrift>
          <Lede>
            Die beiden Praxiskurse sind in acht Bausteine gegliedert, in Krippe und Kita dieselben. Jeder Baustein hat
            ein Einführungsvideo, zwei Lieder mit komplettem Material und weitere Spielideen.
          </Lede>
          <Bausteine />
        </Abschnitt>

        {/* 5. Was zu einem einzigen Lied gehört */}
        <Abschnitt hell>
          <span className={KICKER}>Nicht nur Video</span>
          <Ueberschrift>Was zu einem einzigen Lied gehört</Ueberschrift>
          <Lede>
            Das hier ist der Unterschied zu einem Videokurs. Zu jedem Lied bekommst du das ganze Paket, fertig zum
            Ausdrucken und Mitnehmen in die Gruppe. Klick dich durch vier Beispiele.
          </Lede>
          <Lieder />
        </Abschnitt>

        {/* 6. Das Zertifikat */}
        <Abschnitt>
          <span className={KICKER}>Dein Abschluss</span>
          <Ueberschrift>Das Zertifikat</Ueberschrift>
          <Lede>Am Ende steht ein Zertifikat, das du in deiner Einrichtung und bei Bewerbungen vorlegen kannst.</Lede>
          <div
            className={cn(
              "mt-1.5 rounded-[10px] px-5 py-4 font-display text-[19px] font-extrabold leading-snug text-primary",
              TUERKIS_HAUCH,
            )}
          >
            Fachkraft für Musik und Sprache im Elementarbereich
          </div>
          <div className="mt-6 grid gap-[22px] min-[900px]:grid-cols-2">
            <div className="rounded-[14px] border border-l-[5px] border-l-primary bg-gradient-to-br from-primary/5 to-accent/5 px-6 py-6">
              <h3 className="mb-2 font-display text-lg font-extrabold text-foreground">So arbeitest du dich durch</h3>
              <p className="mb-2.5 text-[15px] text-muted-foreground">
                Du arbeitest die Kurse in deinem Tempo durch. Jeder Kurs schließt mit einem kurzen Wissenscheck ab. Hast
                du alle Kurse durchgearbeitet, schaltet sich die Abschlussprüfung automatisch frei.
              </p>
              <p className="text-[15px] text-muted-foreground">
                Die Abschlussprüfung ist ein Quiz mit 8 Fragen aus allen Kursen. Ab 6 richtigen Antworten hast du
                bestanden. Du kannst es so oft versuchen, wie du möchtest.
              </p>
            </div>
            <div className="rounded-[14px] border bg-card px-6 py-6">
              <h3 className="mb-2 font-display text-lg font-extrabold text-foreground">Was danach passiert</h3>
              <p className="text-[15px] text-muted-foreground">
                Nach der bestandenen Prüfung gibst du uns kurz Feedback zur Ausbildung. Danach ist dein Zertifikat fertig
                und du bekommst es per E-Mail.
              </p>
            </div>
          </div>
        </Abschnitt>

        {/* 7. In drei Schritten zum Zertifikat */}
        <Abschnitt hell>
          <span className={KICKER}>So läuft es</span>
          <Ueberschrift>In drei Schritten zum Zertifikat</Ueberschrift>
          <ol className="mt-6 grid gap-5 min-[900px]:grid-cols-3">
            {[
              ["Kurse durcharbeiten", "Online und in deinem Tempo, so lange du willst. Pro Kurs ein kurzer Wissenscheck."],
              ["Abschlussprüfung", "Ein Quiz mit 8 Fragen aus allen Kursen. Ab 6 richtigen Antworten hast du bestanden."],
              ["Zertifikat", "Nach der bestandenen Prüfung und deinem kurzen Abschluss-Feedback."],
            ].map(([titel, text], i) => (
              <li
                key={titel}
                className="rounded-xl border border-primary/15 bg-gradient-to-br from-primary/5 to-accent/5 p-[22px]"
              >
                <div
                  className={cn(
                    "mb-3 grid h-[34px] w-[34px] place-items-center rounded-full font-display text-[15px] font-extrabold",
                    ETIKETT_TUERKIS,
                  )}
                  aria-hidden="true"
                >
                  {i + 1}
                </div>
                <h3 className="mb-1.5 font-display text-[19px] font-bold text-foreground">{titel}</h3>
                <p className="text-[14.5px] text-muted-foreground">{text}</p>
              </li>
            ))}
          </ol>
        </Abschnitt>

        {/* 7b. Stimmen von Absolventinnen – erst ab MINDESTZAHL_TESTIMONIALS (2) freigegebenen Texten sichtbar */}
        <StimmenAbschnitt />

        {/* 8. Preise und Zahlung */}
        <Abschnitt id="preise">
          <span className={KICKER}>Preise und Zahlung</span>
          <Ueberschrift>Zwei Preise, ein Inhalt</Ueberschrift>
          <Lede>
            Als aktives MusiKonzept-Mitglied zahlst du weniger. Inhalt, Prüfung und Zertifikat sind für alle gleich.
          </Lede>
          <div className="mt-6 grid max-w-[760px] gap-5 min-[900px]:grid-cols-2">
            {mitglied && (
              <Preisblock
                wer="Mit aktiver MusiKonzept-Mitgliedschaft"
                tarif={mitglied}
                aktionAktiv={aktionAktiv}
                text="Gilt, solange deine Mitgliedschaft läuft."
                hervorgehoben
              />
            )}
            {extern && (
              <Preisblock
                wer="Ohne Mitgliedschaft"
                tarif={extern}
                aktionAktiv={aktionAktiv}
                text="Der reguläre Preis für alle anderen."
              />
            )}
          </div>
          <div className="mt-5 max-w-[760px] rounded-xl border bg-card px-[22px] py-[18px] text-[15px] text-muted-foreground">
            <b className="text-foreground">Zahlbar per Rechnung</b> in voller Höhe innerhalb von 14 Tagen, oder{" "}
            <b className="text-foreground">in 6 Monatsraten per SEPA-Lastschrift ohne Aufschlag</b>. Die Raten ergeben
            denselben Preis, als Mitglied zum Beispiel{" "}
            {ratenMitglied
              ? `${ratenMitglied.installmentCount} × ${formatEuro(ratenMitglied.baseInstallmentCents)}`
              : ""}
            . Die Zahlung startet mit deiner Anmeldung, dein Zugang ist sofort frei.
          </div>
          <div className="mt-6">
            {buchbar ? (
              <Button
                size="lg"
                className="group rounded-full bg-accent px-8 text-accent-foreground shadow-xl transition-all duration-300 hover:scale-105 hover:bg-accent/90 hover:shadow-2xl"
                asChild
              >
                <Link to={CHECKOUT_PFAD}>
                  Jetzt anmelden
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            ) : (
              <Button size="lg" disabled className="rounded-full px-8">
                Jetzt anmelden
              </Button>
            )}
          </div>
        </Abschnitt>

        {/* 9. FAQ */}
        <Abschnitt hell>
          <span className={KICKER}>Häufige Fragen</span>
          <Ueberschrift>Was du sonst noch wissen willst</Ueberschrift>
          <Accordion type="multiple" className="mt-2 space-y-2.5">
            {faq().map((f) => (
              <AccordionItem key={f.frage} value={f.frage} className="rounded-xl border bg-card px-5">
                <AccordionTrigger className="text-left font-display text-base font-bold text-foreground hover:no-underline">
                  {f.frage}
                </AccordionTrigger>
                <AccordionContent className="text-[15px] text-muted-foreground">{f.antwort}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Abschnitt>
      </main>

      <Footer />
    </div>
  );
}

/* ─────────────────────────────── Bausteine */

/**
 * Acht Reiter, ein Feld darunter. Radix-Tabs liefern `role="tab"`,
 * `aria-selected` und die Pfeiltasten-Bedienung. Mobil bricht das Raster auf
 * vier, dann auf zwei Spalten um (Auftrag, Abschnitt 3).
 */
function Bausteine() {
  return (
    <TabsPrimitive.Root defaultValue="0" className="mt-6">
      <TabsPrimitive.List
        aria-label="Die acht Bausteine"
        className="mb-[22px] grid grid-cols-2 gap-2.5 min-[521px]:grid-cols-4 min-[901px]:grid-cols-8"
      >
        {BAUSTEINE.map((b, i) => (
          <TabsPrimitive.Trigger
            key={b.name}
            value={String(i)}
            className={cn(
              "rounded-[14px] border-[1.5px] bg-card px-1.5 pb-2.5 pt-3 text-center text-foreground transition-[border-color,transform] duration-150 hover:-translate-y-0.5",
              "aria-selected:border-[#49BDC2] aria-selected:bg-[#EAF6F7]",
              FOKUS,
            )}
          >
            <img src={bausteinIcon(b.icon)} alt="" className="mx-auto mb-[7px] block h-[52px] w-[52px] object-contain" />
            <b className="block hyphens-auto break-words text-center font-display text-[11.5px] font-bold leading-tight">{b.name}</b>
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>

      {BAUSTEINE.map((b, i) => (
        <TabsPrimitive.Content
          key={b.name}
          value={String(i)}
          className={cn("rounded-2xl border bg-card p-5 shadow-sm min-[900px]:p-8", FOKUS)}
        >
          <div className="mb-[22px] flex items-start gap-[18px]">
            <img src={bausteinIcon(b.icon)} alt="" className="h-[76px] w-[76px] flex-none object-contain" />
            <div>
              <span className={KICKER}>
                Baustein {i + 1} von {BAUSTEINE.length}
              </span>
              <h3 className="mb-1.5 font-display text-[25px] font-extrabold leading-tight text-foreground">{b.name}</h3>
              <p className="text-muted-foreground">{b.beschreibung}</p>
            </div>
          </div>
          <div className="grid gap-[22px] min-[900px]:grid-cols-2">
            <LektionsListe wer="Kati Breuer" alter="Krippe · U3" titel={b.u3} />
            <LektionsListe wer="Nicole Knorr" alter="Kita · Ü3" titel={b.ue3} />
          </div>
        </TabsPrimitive.Content>
      ))}
    </TabsPrimitive.Root>
  );
}

function LektionsListe({ wer, alter, titel }: { wer: string; alter: string; titel: readonly string[] }) {
  return (
    <div className={cn("rounded-xl px-5 py-[18px]", FLAECHE_HELL)}>
      <div className="mb-0.5 font-display text-xs font-bold uppercase tracking-[0.09em] text-[#B5720A]">{wer}</div>
      <div className="mb-3 font-display text-[17px] font-extrabold text-foreground">{alter}</div>
      <ul>
        {titel.map((t) => (
          <li
            key={t}
            className="relative border-t border-[#E6E3DD] py-[7px] pl-6 text-[15px] first:border-t-0 before:absolute before:left-1 before:top-[15px] before:h-[7px] before:w-[7px] before:rounded-full before:bg-[#49BDC2]"
          >
            {t}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ─────────────────────────────── Lieder */

function Lieder() {
  return (
    <TabsPrimitive.Root defaultValue="0" className="mt-1">
      <TabsPrimitive.List aria-label="Vier Beispiel-Lieder" className="mb-5 flex flex-wrap gap-2">
        {LIEDER.map((l, i) => (
          <TabsPrimitive.Trigger
            key={l.titel}
            value={String(i)}
            className={cn(
              "rounded-full border-[1.5px] bg-card px-4 py-[9px] text-sm font-semibold leading-none text-foreground",
              "aria-selected:border-[#49BDC2] aria-selected:bg-[#49BDC2] aria-selected:text-[#12494C]",
              FOKUS,
            )}
          >
            {l.titel}
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>

      <div className="grid items-start gap-[26px] min-[900px]:grid-cols-[1.1fr_0.9fr]">
        <div>
          {LIEDER.map((l, i) => (
            <TabsPrimitive.Content
              key={l.titel}
              value={String(i)}
              className={cn("overflow-hidden rounded-2xl border bg-card shadow-sm", FOKUS)}
            >
              <LiedVorschau lied={l} />
              <div className="px-[22px] pb-6 pt-5">
                <span className={KICKER}>{l.herkunft}</span>
                <h3 className="font-display text-[22px] font-bold text-foreground">{l.titel}</h3>
                <ul className="mt-3.5 grid gap-[9px]">
                  {l.material.map((mat) => (
                    <li
                      key={mat.art + mat.text}
                      className={cn("flex items-center gap-3 rounded-[10px] px-3.5 py-[11px]", FLAECHE_HELL)}
                    >
                      <span
                        className={cn(
                          "min-w-[82px] flex-none rounded-[5px] px-2 py-[5px] text-center font-display text-[10.5px] font-bold uppercase leading-none tracking-[0.07em]",
                          mat.betont ? ETIKETT_ORANGE : ETIKETT_TUERKIS,
                        )}
                      >
                        {mat.art}
                      </span>
                      <span className="text-[14.5px]">{mat.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </TabsPrimitive.Content>
          ))}
        </div>

        <div>
          <h3 className="mb-1.5 font-display text-[19px] font-bold text-foreground">Alles liegt fertig bereit</h3>
          <p className="mb-3.5">
            Du musst nichts vorbereiten, nichts abtippen und nichts suchen. Noten als PDF, das Lied als Audio zum
            Mitsingen, Bildkarten zum Ausdrucken und eine Spielidee, die beschreibt, wie du es in deiner Gruppe machst.
          </p>
          <p className="mb-3.5">
            Gerade wenn du selbst kein Instrument spielst: Die Audios sind so gebaut, dass du sie einfach laufen lassen
            und mitmachen kannst.
          </p>
          <div className="mt-1.5 rounded-xl border bg-card px-[22px] py-[18px] text-[15px] text-muted-foreground">
            <b className="text-foreground">Dauerhafter Zugang.</b> Kein Ablaufdatum, keine Zwölf-Monats-Frist. Das
            Material bleibt dir, auch nach dem Zertifikat.
          </div>
        </div>
      </div>
    </TabsPrimitive.Root>
  );
}

/** So lange läuft die Videovorschau, bevor das Anmelde-Fenster erscheint. */
const VORSCHAU_SEKUNDEN = 30;

/**
 * Standbild mit Play-Zeichen. Solange `lied.vimeo` leer ist, bleibt es ein
 * Bild. Mit einer Vimeo-Angabe wird es ein Knopf, der beim Klick den Player
 * einsetzt: kein Autoplay und kein Ton beim Laden der Seite.
 *
 * Die Vorschau läuft nur 30 Sekunden: Dann pausiert der Player (per
 * postMessage an den Vimeo-iframe) und ein Fenster „Jetzt anmelden" legt
 * sich darüber, das zur Kaufseite führt. So bekommen Besucher:innen einen
 * echten Eindruck, das volle Video bleibt aber Mitgliedern vorbehalten.
 */
function LiedVorschau({ lied }: { lied: Lied }) {
  const [spielt, setSpielt] = useState(false);
  const [vorschauZuEnde, setVorschauZuEnde] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const player = vimeoPlayerUrl(lied.vimeo);

  useEffect(() => {
    if (!spielt || vorschauZuEnde) return;
    const timer = window.setTimeout(() => {
      iframeRef.current?.contentWindow?.postMessage(
        vimeoPausenNachricht(),
        "https://player.vimeo.com",
      );
      setVorschauZuEnde(true);
    }, VORSCHAU_SEKUNDEN * 1000);
    return () => window.clearTimeout(timer);
  }, [spielt, vorschauZuEnde]);

  if (player && spielt) {
    return (
      <div className="relative aspect-video w-full bg-black">
        <iframe
          ref={iframeRef}
          src={player}
          title={`Video zu ${lied.titel}`}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 h-full w-full border-0"
        />
        {vorschauZuEnde && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-[rgba(18,73,76,0.92)] px-6 text-center">
            <p className="text-lg font-bold text-white sm:text-xl">
              Das war die Vorschau
            </p>
            <p className="max-w-sm text-sm leading-relaxed text-white/85">
              Das komplette Video mit Lied, Material und Spielideen siehst du
              in der Ausbildung.
            </p>
            <Button
              asChild
              size="lg"
              className="rounded-full bg-[#F5A623] px-8 font-bold text-[#12494C] shadow-[0_10px_25px_-8px_rgba(245,166,35,0.7)] hover:bg-[#e0951a]"
            >
              <Link to={CHECKOUT_PFAD}>Jetzt anmelden</Link>
            </Button>
          </div>
        )}
      </div>
    );
  }

  const inhalt = (
    <>
      <img
        src={lied.standbild}
        alt={`Standbild aus dem Video zu ${lied.titel}`}
        loading="lazy"
        className="block aspect-video w-full max-w-full object-cover"
      />
      <span
        className="absolute inset-0 grid place-items-center bg-gradient-to-b from-black/5 to-black/30"
        aria-hidden="true"
      >
        <span className="grid h-16 w-16 place-items-center rounded-full bg-white/95 shadow-[0_4px_18px_rgba(0,0,0,0.28)]">
          <span className="ml-[5px] h-0 w-0 border-y-[12px] border-l-[19px] border-y-transparent border-l-[#226C70]" />
        </span>
      </span>
      <span className="absolute bottom-3 left-3 rounded-md bg-[rgba(18,73,76,0.86)] px-2.5 py-[5px] text-xs font-semibold leading-none text-white">
        {lied.videoLabel}
      </span>
    </>
  );

  return player ? (
    <button
      type="button"
      onClick={() => setSpielt(true)}
      aria-label={`Video zu ${lied.titel} abspielen`}
      className={cn("relative block w-full", FOKUS)}
    >
      {inhalt}
    </button>
  ) : (
    <div className="relative">{inhalt}</div>
  );
}

/* ─────────────────────────────── Preise */

/**
 * Eine Preissäule. Der Platz für den Frühbucher-Hinweis ist Teil des Blocks:
 * Läuft die Aktion laut Server, steht der Aktionspreis groß da, der reguläre
 * durchgestrichen daneben und die Frist darunter. Sonst bleibt es bei
 * 490 € bzw. 590 €, ohne dass sich das Layout verschiebt.
 */
function Preisblock({
  wer,
  tarif,
  aktionAktiv,
  text,
  hervorgehoben = false,
}: {
  wer: string;
  tarif: TarifPrices;
  aktionAktiv: boolean;
  text: string;
  hervorgehoben?: boolean;
}) {
  const zahlbar = anzeigeRechnung(tarif, aktionAktiv).amountCents;
  const rabatt = aktionRabattCents(tarif, aktionAktiv);

  return (
    <div
      className={cn(
        "flex flex-col rounded-[14px] border bg-gradient-to-br from-primary/5 to-accent/5 px-7 py-[26px]",
        hervorgehoben && "border-[#49BDC2] shadow-[0_0_0_3px_rgba(73,189,194,0.22)]",
      )}
    >
      <div className="font-display text-[13px] font-bold leading-snug text-[#B5720A]">{wer}</div>
      <div className="mb-3 mt-2.5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-display text-[42px] font-extrabold leading-none text-foreground [font-variant-numeric:tabular-nums]">
          {euro(zahlbar)}
        </span>
        {rabatt > 0 && (
          <span className="text-xl text-muted-foreground line-through">{euro(tarif.rechnung.amountCents)}</span>
        )}
      </div>
      {rabatt > 0 && (
        <p className={cn("mb-2.5 rounded-md px-2.5 py-1.5 text-[13px] font-semibold text-primary", TUERKIS_HAUCH)}>
          {euro(rabatt)} Frühbucher-Rabatt{" "}
          <span className="whitespace-nowrap">bis {AKTION_ENDE_LABEL}</span>
        </p>
      )}
      <p className="flex-1 text-[14.5px] text-muted-foreground">{text}</p>
    </div>
  );
}

/* ─────────────────────────────── Bausteine der Seite */

/**
 * „Das sagen Absolventinnen“: Schnitt und Anzahl über ALLE abgegebenen Bewertungen (nicht nur die
 * freigegebenen), darunter die freigegebenen Texte mit Einwilligung. Unsichtbar, solange es weniger
 * als 3 freigegebene Texte gibt. Bewusst ohne Schema.org-Bewertungen (nicht ohne Daniels OK).
 */
function StimmenAbschnitt() {
  const { data } = useQuery({
    queryKey: ["ausbildung-bewertungen-oeffentlich", TRAINING_BUNDLE_IDS.FACHKRAFT_MUSIK_SPRACHE],
    queryFn: () => bewertungenDb.oeffentlich(TRAINING_BUNDLE_IDS.FACHKRAFT_MUSIK_SPRACHE),
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  if (!zeigeBewertungsAbschnitt(data)) return null;

  return (
    <Abschnitt hell>
      <span className={KICKER}>Stimmen</span>
      <Ueberschrift>Das sagen Absolventinnen</Ueberschrift>
      <p className="mb-7 flex flex-wrap items-center gap-x-3 gap-y-1 text-lg text-muted-foreground">
        <span className="flex items-center gap-0.5" aria-hidden>
          {[1, 2, 3, 4, 5].map((n) => (
            <Star
              key={n}
              className={cn("h-5 w-5", n <= Math.round(data.durchschnitt ?? 0) ? "fill-[#F5A623] text-[#F5A623]" : "text-muted-foreground/30")}
            />
          ))}
        </span>
        <span>
          <strong className="font-semibold text-foreground">{durchschnittText(data.durchschnitt ?? 0)} von 5</strong>{" "}
          aus {data.anzahl} {data.anzahl === 1 ? "Bewertung" : "Bewertungen"}
        </span>
      </p>
      <ul className="grid gap-4 min-[700px]:grid-cols-2 min-[1000px]:grid-cols-3">
        {data.testimonials.map((t, i) => (
          <li key={i} className="flex flex-col rounded-2xl border border-border/50 bg-background p-5 shadow-sm">
            <span className="mb-3 flex gap-0.5" aria-label={`${t.sterne} von 5 Sternen`}>
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} className={cn("h-4 w-4", n <= t.sterne ? "fill-[#F5A623] text-[#F5A623]" : "text-muted-foreground/30")} />
              ))}
            </span>
            <blockquote className="mb-4 flex-1 whitespace-pre-line text-[15px] leading-relaxed text-foreground">
              „{t.text}“
            </blockquote>
            <p className="text-sm font-semibold text-foreground">
              {t.anzeigename}
              {t.ort && <span className="font-normal text-muted-foreground">, {t.ort}</span>}
            </p>
          </li>
        ))}
      </ul>
    </Abschnitt>
  );
}

function Abschnitt({
  children,
  id,
  hell = false,
}: {
  children: ReactNode;
  id?: string;
  hell?: boolean;
}) {
  return (
    <section
      id={id}
      className={cn(
        // Die Kopfzeile ist fest positioniert und 80 px hoch: Sprungmarken
        // (#kurse, #preise) landen nicht unter ihr.
        "scroll-mt-24 border-t border-border/40 py-11 min-[900px]:py-[76px]",
        // Helle Abschnitte bekommen den sanften Farbverlauf der Geschwisterseiten.
        hell && "border-t-0 bg-gradient-to-b from-muted/30 via-background to-background",
      )}
    >
      <div className="mx-auto max-w-[1120px] px-4 min-[421px]:px-5">{children}</div>
    </section>
  );
}

function Ueberschrift({ children }: { children: ReactNode }) {
  return (
    <h2 className="mb-2.5 font-display text-[clamp(24px,3.4vw,34px)] font-extrabold leading-tight tracking-[-0.015em] text-foreground [text-wrap:balance]">
      {children}
    </h2>
  );
}

function Lede({ children }: { children: ReactNode }) {
  return <p className="mb-3.5 max-w-[66ch] text-lg text-muted-foreground">{children}</p>;
}
